import { Box, Flex, useColorModeValue } from "@chakra-ui/react";
import { ErrorState } from "../../../../components/ErrorState";
import { StatsGrid } from "./StatsGrid";
import { CandidateListToolbar } from "./CandidateListToolbar";
import { CandidateSelectAll } from "./CandidateSelectAll";
import { Kanban } from "./Kanban";
import type { KanbanColumnData } from "./KanbanColumn";
import { CandidateSectionAccordion, type SectionGroup } from "./CandidateSectionAccordion";
import type { ViewMode } from "./ViewToggle";
import type { Candidate, OnboardingStats } from "../../../../types/onboarding";
import type { WhitelistEntry } from "../../../../types/whitelist";
import type { HrProcessStats } from "../../../../lib/api";

interface HrProcessesTabProps {
  hrStats: HrProcessStats | null;
  isLoadingHr: boolean;
  hrError: boolean;
  columns: KanbanColumnData[];
  sectionGroups: SectionGroup[];
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onSectionExpand: (key: string) => void;
  search: string;
  onSearchChange: (v: string) => void;
  onExport: () => void;
  onCartaProposta?: () => void;
  onViewDetail: (c: Candidate) => void;
  onCancel: (c: Candidate) => void;
  onReactivate: (c: Candidate) => void;
  onRetry: () => void;
  whitelistMap?: Record<string, WhitelistEntry>;
  selectedCandidateIds: Set<string | number>;
  onToggleSelect: (id: string | number) => void;
  onSelectAll?: () => void;
  candidateCount?: number;
}

export const HrProcessesTab = ({
  hrStats, isLoadingHr, hrError,
  columns, sectionGroups, viewMode, onViewModeChange, onSectionExpand,
  search, onSearchChange, onExport, onCartaProposta,
  onViewDetail, onCancel, onReactivate,
  onRetry,
  whitelistMap = {},
  selectedCandidateIds,
  onToggleSelect,
  onSelectAll,
  candidateCount = 0,
}: HrProcessesTabProps) => {
  const dividerColor = useColorModeValue("gray.100", "whiteAlpha.80");

  const hrStatsForGrid: OnboardingStats = {
    total: hrStats?.total ?? 0,
    em_andamento: hrStats?.em_andamento ?? 0,
    aguardando: 0,
    aguardando_rh: hrStats?.aguardando_rh ?? 0,
    concluidos: hrStats?.concluido ?? 0,
    cancelados: hrStats?.cancelado ?? 0,
  };

  return (
    <Flex direction="column" h="calc(100vh - 160px)" minH="400px">
      <Box flexShrink={0}>
        <StatsGrid
          stats={hrStatsForGrid}
          isLoading={isLoadingHr || !hrStats}
          showOnly={["aguardando_rh", "em_andamento", "concluidos", "cancelados"]}
          labels={{ aguardando_rh: "Não Iniciado" }}
        />

        {candidateCount > 0 && onSelectAll && (
          <CandidateSelectAll
            selectedCount={selectedCandidateIds.size}
            totalCount={candidateCount}
            onToggle={onSelectAll}
          />
        )}

        <CandidateListToolbar
          search={search} onSearchChange={onSearchChange}
          selectedCount={selectedCandidateIds.size}
          onExport={onExport}
          onCartaProposta={onCartaProposta}
          viewMode={viewMode} onViewModeChange={onViewModeChange}
        />

        <Box borderTopWidth="1px" borderColor={dividerColor} mb={4} />
      </Box>

      {hrError ? (
        <ErrorState onRetry={onRetry} />
      ) : viewMode === "kanban" ? (
        <Box flex={1} overflow="hidden" minH={0}>
          <Kanban
            columns={columns}
            selectedCandidateIds={selectedCandidateIds}
            onToggleSelect={onToggleSelect}
            onViewDetail={onViewDetail}
            onCancel={onCancel}
            onReactivate={onReactivate}
            showHrProgress
            whitelistMap={whitelistMap}
          />
        </Box>
      ) : (
        <CandidateSectionAccordion
          sections={sectionGroups}
          defaultOpenKey="aguardando_rh"
          onExpand={onSectionExpand}
          selectedCandidateIds={selectedCandidateIds}
          onToggleSelect={onToggleSelect}
          onViewDetail={onViewDetail}
          onCancel={onCancel}
          onReactivate={onReactivate}
          showHrProgress
          whitelistMap={whitelistMap}
        />
      )}
    </Flex>
  );
};
